import React from "react";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import { CloseIcon, CloseIconThin } from "components/Icons";
import GradientGhostButton from "components/Buttons/GradientGhostButton";
import { PrimaryButton } from "components/mui-styled-components/Buttons";

import { cvar } from "styles";
import { theme } from "styles/themes/theme";

type HandleCloseT = {
  handleClose: () => void;
  handleCancel?: () => void;
};

type HandleCancelT = {
  handleClose?: () => void;
  handleCancel: () => void;
};

type HandleCloseOrHandleCancelT = HandleCloseT | HandleCancelT;

type ConfirmModalPropsT = {
  open: boolean;
  title: string | React.ReactNode;
  handleConfirm: () => void;
  onClose?: () => void;
  children?: React.ReactNode;
  titleStyles?: React.CSSProperties;
  cancelButton?: boolean;
  confirmButtonText?: string;
  buttonHeight?: number;
  justifyFooterContent?: "flex-start" | "center" | "flex-end";
  maxWidth?: number;
  confirmDisabled?: boolean;
  disabledReason?: string;
  cancelButtonText?: string;
  showCornerCloseButton?: boolean;
} & HandleCloseOrHandleCancelT;

const ConfirmModal = ({
  onClose,
  open,
  children,
  handleConfirm,
  handleClose,
  title,
  titleStyles = {},
  handleCancel = () => null,
  cancelButton = true,
  confirmButtonText = "Accept",
  buttonHeight = 32,
  justifyFooterContent = "flex-end",
  maxWidth,
  confirmDisabled = false,
  disabledReason,
  cancelButtonText = "",
  showCornerCloseButton = false,
}: ConfirmModalPropsT) => {
  const onCancel = () => {
    handleCancel();
    if (onClose) onClose();
  };
  const onConfirm = () => {
    handleConfirm();
  };

  const handleCloseWithoutCancel = () => {
    if (handleClose) {
      handleClose();
    } else {
      onCancel();
    }
  };

  return (
    <Dialog
      onClose={handleCloseWithoutCancel}
      open={open}
      maxWidth="md"
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          ...(maxWidth ? { maxWidth } : {}),
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: "32px 22px 24px 22px",
          background: cvar("white"),
          color: cvar("blue-ribbon"),
          fontFamily: "Poppins",
          fontWeight: "700",
          fontSize: "18px",
          lineHeight: "21px",
          textTransform: "capitalize",
          display: "flex",
          justifyContent: "space-between",
          ...titleStyles,
        }}
      >
        <div>{title}</div>
        {showCornerCloseButton && (
          <IconButton aria-label="close" onClick={handleCloseWithoutCancel}>
            <CloseIconThin fill={theme.colors["queen-blue"]} />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: "24px" }}>{children}</DialogContent>
      <DialogActions
        sx={{
          justifyContent: justifyFooterContent,
          padding: "14px 22px",
          background: cvar("dark-blue-a07"),
        }}
      >
        {cancelButton && (
          <>
            {cancelButtonText ? (
              <PrimaryButton
                onClick={onCancel}
                variant="outlined"
                style={{ minWidth: "164px", height: buttonHeight, padding: "8px 24px" }}
              >
                {cancelButtonText}
              </PrimaryButton>
            ) : (
              <GradientGhostButton onClick={onCancel} style={{ width: buttonHeight, height: buttonHeight }}>
                <CloseIcon startFill={cvar("picton-blue")} stopFill={cvar("blue-ribbon")} />
              </GradientGhostButton>
            )}
          </>
        )}
        <PrimaryButton
          onClick={onConfirm}
          gradient
          style={{ minWidth: "164px", marginLeft: "10px", height: buttonHeight, padding: "8px 24px" }}
          disabled={confirmDisabled}
          tooltipTitle={confirmDisabled ? disabledReason : ""}
        >
          {confirmButtonText}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmModal;
